import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";

import { ContactEmail } from "@/components/ui/ContactEmail";
import { PageHeader } from "@/components/ui/PageHeader";
import { Section } from "@/components/ui/Section";
import { useLocale } from "@/i18n";

/**
 * Privacy policy and terms of use.
 *
 * The content lives in the i18n catalogues, Arabic-first, like every other page.
 *
 * TWO THINGS ABOUT THESE DOCUMENTS
 *
 * **They are accurate, and they are not legally reviewed.** Every factual claim
 * here describes something that is actually implemented — local storage, the five
 * processing modes and their server-side enforcement, keys never reaching the
 * client, no telemetry in Offline. None of it is aspirational. But describing data
 * handling truthfully is not the same as having a lawyer confirm the document
 * satisfies a given jurisdiction, so {@link DraftNotice} says so at the top of
 * both, prominently rather than in a footnote.
 *
 * **A claim here is a commitment in the code.** If Offline mode ever gains a
 * fallback path, or telemetry starts firing in it, this page becomes a false
 * statement about a privacy guarantee. Treat these two files as coupled.
 */

/** Placeholder until the documents are finalised and dated for real. */
const LAST_UPDATED = "2026-08-13";

function DraftNotice() {
  const { t } = useLocale();
  return (
    <div className="mb-12 flex items-start gap-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] p-5">
      <AlertTriangle
        className="mt-0.5 size-5 shrink-0 text-amber-300/80"
        aria-hidden
      />
      <p className="text-sm leading-relaxed text-cream-100/70">
        {t.legal.draftNotice}
      </p>
    </div>
  );
}

function Clause({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="border-t border-white/8 py-8 first:border-t-0 first:pt-0">
      <h2 className="mb-3 text-lg font-medium text-cream-50">{heading}</h2>
      <div className="space-y-3 text-[0.95rem] leading-relaxed text-cream-100/65">
        {children}
      </div>
    </section>
  );
}

function ContactLine({ body }: { body: string }) {
  return (
    <p>
      {body}{" "}
      <ContactEmail className="text-cream-50 underline decoration-white/25 underline-offset-4 transition-colors hover:decoration-white/60" />
    </p>
  );
}

/** Shared frame: header, date, draft notice, then the clauses. */
function LegalLayout({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: ReactNode;
}) {
  const { t } = useLocale();
  return (
    <>
      <PageHeader title={title} subtitle={intro} />
      <Section>
        <div className="mx-auto max-w-3xl">
          <p className="mb-8 text-sm text-cream-100/40">
            {t.legal.updated}: {LAST_UPDATED}
          </p>
          <DraftNotice />
          {children}
        </div>
      </Section>
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function PrivacyPage() {
  const { t } = useLocale();
  const p = t.legal.privacy;

  return (
    <LegalLayout title={p.title} intro={p.intro}>
      <Clause heading={p.localTitle}>
        <p>{p.localBody}</p>
      </Clause>

      <Clause heading={p.modesTitle}>
        <p>{p.modesBody}</p>
        {/*
          Each mode is listed separately rather than summarised, because the
          differences between them are the substance of the privacy promise. A
          reader choosing Offline needs to see exactly what it refuses.
        */}
        <ul className="mt-4 space-y-3">
          {[p.modeLocal, p.modeOffline, p.modeHybrid, p.modeCloud].map(
            (line) => (
              <li key={line} className="flex gap-3">
                <span
                  className="mt-[0.6rem] size-1.5 shrink-0 rounded-full bg-cream-100/30"
                  aria-hidden
                />
                <span>{line}</span>
              </li>
            ),
          )}
        </ul>
      </Clause>

      <Clause heading={p.keysTitle}>
        <p>{p.keysBody}</p>
      </Clause>

      <Clause heading={p.collectTitle}>
        <p>{p.collectBody}</p>
      </Clause>

      <Clause heading={p.rightsTitle}>
        <p>{p.rightsBody}</p>
      </Clause>

      <Clause heading={p.contactTitle}>
        <ContactLine body={p.contactBody} />
      </Clause>
    </LegalLayout>
  );
}

/* -------------------------------------------------------------------------- */

export function TermsPage() {
  const { t } = useLocale();
  const s = t.legal.terms;

  return (
    <LegalLayout title={s.title} intro={s.intro}>
      <Clause heading={s.serviceTitle}>
        <p>{s.serviceBody}</p>
      </Clause>

      <Clause heading={s.accountTitle}>
        <p>{s.accountBody}</p>
      </Clause>

      <Clause heading={s.useTitle}>
        <p>{s.useBody}</p>
      </Clause>

      <Clause heading={s.aiTitle}>
        <p>{s.aiBody}</p>
      </Clause>

      <Clause heading={s.contentTitle}>
        <p>{s.contentBody}</p>
      </Clause>

      <Clause heading={s.openSourceTitle}>
        <p>{s.openSourceBody}</p>
      </Clause>

      <Clause heading={s.changesTitle}>
        <p>{s.changesBody}</p>
      </Clause>

      <Clause heading={s.contactTitle}>
        <ContactLine body={s.contactBody} />
      </Clause>
    </LegalLayout>
  );
}
